import { ActionButton } from '@emerald-works-nova/components'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { Box, Backdrop, CircularProgress, FormControlLabel, Grid, Switch, Typography } from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import React from 'react'
import { useSelector } from 'react-redux'
import { BannerStatic } from '..'
import { usePromiseEvent } from '../../hooks'
import { TenantEditSlice } from '../../reducers'
import ManageCategoryModal from '../manage-category'
import { AddCategoryModal } from './add-category-modal'
import CategoryItem from './category-item'
import { EditCategoryModal } from './edit-category-modal'
import { HandleCategoryMove } from './move-category-modal'
import { useStyles } from './style'

const TemplateHierarchy = ({
  viewOnly,
  template,
  templateSK,
  onChangeTemplate
}) => {
  const classes = useStyles()

  const [hierarchy, setHierarchy] = React.useState([])
  const [openAll, setOpenAll] = React.useState(false)
  const [openManageCategory, setOpenManageCategory] = React.useState(false)
  const [openMoveCategory, setOpenMoveCategory] = React.useState(false)
  const [openAddCategory, setOpenAddCategory] = React.useState(false)
  const [addCategoryData, setAddCategoryData] = React.useState()
  const [openRenameCategory, setOpenRenameCategory] = React.useState(false)
  const [renameCategoryData, setRenameCategoryData] = React.useState({})
  const [managedCategory, setManagedCategory] = React.useState()
  const [moveCategoryData, setMoveCategoryData] = React.useState()
  const [isLoading, setIsLoading] = React.useState(false)
  const [isLoadingBackdrop, setIsLoadingBackdrop] = React.useState(false)
  const [newParentCategorySk, setNewParentCategorySk] = React.useState(null)
  const [newCategoryPosition, setNewCategoryPosition] = React.useState(0)

  const [catGetGroupCatByTemplate] = useEvent([{
    eventName: 'catGetGroupCatByTemplate',
    onSuccess: ({ categories }) => {
      setIsLoading(false)
      setHierarchy(categories)
    },
    onError: (...args) => {
      console.error('catGetGroupCatByTemplate.registerAdhocOnErrorListener', args)
      BannerStatic.show({ label: 'Error fetching hierarchy', isError: true })
    }
  }])

  const [categoryAddToTemplate, categoryRename] = useEvent([
    {
      eventName: 'categoryAddToTemplate',
      onSuccess: () => {
        setOpenAddCategory(false)
        getGroupCategoriesDelayed()
        BannerStatic.show({ label: 'The Categories were successfully created.' })
      },
      onError: (...args) => {
        console.error('categoryAddToTemplate.registerAdhocOnErrorListener', args)
        BannerStatic.show({ label: 'Error adding category', isError: true })
        setOpenAddCategory(false)
      }
    },
    {
      eventName: 'deltaInsert',
      onSuccess: () => {
        setOpenRenameCategory(false)
        getGroupCategoriesDelayed()
        BannerStatic.show({ label: 'The Category was successfully edited.' })
      },
      onError: (...args) => {
        console.error('categoryRename.registerAdhocOnErrorListener', args)
        BannerStatic.show({ label: 'Error adding category', isError: true })
        setOpenRenameCategory(false)
      }
    }
  ])

  const tenant = useSelector(TenantEditSlice.selectors.tenant)
  template = template || { sk: templateSK, tenantId: tenant.pk }

  const categoryToggle = usePromiseEvent('deltaInsert')
  const categoryUpdatePosition = usePromiseEvent('categoryUpdatePosition')
  const categoryUpdateParenthood = usePromiseEvent('categoryUpdateParenthood')

  const getGroupCategories = React.useCallback(() => {
    catGetGroupCatByTemplate.trigger({ templateSK: template?.sk || templateSK, tenantId: template?.tenantId })
  }, [catGetGroupCatByTemplate, template.sk, template.tenantId, templateSK])

  const getGroupCategoriesDelayed = React.useCallback(() => {
    setIsLoading(true)
    setTimeout(getGroupCategories, 5000)
  }, [getGroupCategories])

  useEventsOnViewLoad(() => {
    !catGetGroupCatByTemplate.hasBeenTriggered && getGroupCategories()
  }, [catGetGroupCatByTemplate, getGroupCategories])

  const handleOpenAll = React.useCallback((_, v) => setOpenAll(v), [])

  const onManageCategory = React.useCallback(category => {
    setManagedCategory(category)
    setOpenManageCategory(true)
  }, [setManagedCategory, setOpenManageCategory])

  const onMoveCategory = (category, parentCategory, hierarchy) => {
    setMoveCategoryData({ category, parentCategory, hierarchy })
    setOpenMoveCategory(true)
  }

  const onOpenAddCategory = ({ parentCat, type, startPosition }) => {
    setAddCategoryData({ parentCat, type, startPosition })
    setOpenAddCategory(true)
  }

  const handleCloseCatModal = React.useCallback(({ entries, parentCat, startPosition }) => {
    let position = startPosition + 1
    categoryAddToTemplate.trigger({
      categories: entries.map(entry => ({ ...entry, position: position++ })),
      template: template.sk,
      parent: parentCat
    })
    setAddCategoryData({})
  }, [categoryAddToTemplate, template.sk])

  const handleCancelCatModal = () => {
    setOpenAddCategory(false)
    setAddCategoryData({})
  }

  const onOpenRenameCategory = (category) => {
    setRenameCategoryData(category)
    setOpenRenameCategory(true)
  }

  const handleCancelRenameCatModal = () => {
    setOpenRenameCategory(false)
    setRenameCategoryData({})
  }

  const handleCloseRenameCatModal = React.useCallback((category, name, description, imageInfo, hideFromLPQ) => {
    categoryRename.trigger({
      deltaType: 'category',
      template: template.sk,
      category: category.sk,
      metadata: { name, description, imageInfo, hideFromLPQ }
    })
    setRenameCategoryData({})
  }, [categoryRename, template.sk])

  const onSuccessCategoryUpdatePosition = React.useCallback(category => {
    BannerStatic.show({ label: 'The Category was successfully ordered.' })
    const categoryList = (category?.children || hierarchy).sort((a, b) => a.position - b.position)
    if (category) {
      category.children = categoryList
      setHierarchy(state => ([...state]))
    } else setHierarchy(categoryList)
  }, [hierarchy])

  const onSuccessCategoryUpdateParenthood = React.useCallback(newHierarchy => {
    setOpenMoveCategory(false)
    BannerStatic.show({ label: 'The Category was successfully moved.' })
    setHierarchy(newHierarchy)
  }, [])

  const onErrorCategoryUpdatePosition = (...args) => {
    console.error('categoryUpdatePosition.registerAdhocOnErrorListener', args)
    BannerStatic.show({ label: 'Error ordering category', isError: true })
  }

  const onErrorCategoryUpdateParenthood = (...args) => {
    console.error('categoryUpdateParenthood.registerAdhocOnErrorListener', args)
    BannerStatic.show({ label: 'Error moving category', isError: true })
  }

  const onOrderUpCategory = React.useCallback((category, previousCategory, parentCategory) => {
    setIsLoadingBackdrop(true)

    // odd cases when two categories has same position value
    if (previousCategory.position === category.position) {
      category.position = category.position + 1
    }

    const newCategoryPosition = previousCategory.position
    const oldCategoryPosition = category.position

    category.position = newCategoryPosition
    previousCategory.position = oldCategoryPosition

    const categoriesToUpdatePosition = [previousCategory, category]

    categoryUpdatePosition({ template: template.sk, categories: categoriesToUpdatePosition })
      .then(() => {
        onSuccessCategoryUpdatePosition(parentCategory)
      }, onErrorCategoryUpdatePosition)
      .finally(() => setIsLoadingBackdrop(false))
  }, [template.sk, categoryUpdatePosition, onSuccessCategoryUpdatePosition])

  const onOrderDownCategory = React.useCallback((category, nextCategory, parentCategory) => {
    setIsLoadingBackdrop(true)

    // odd cases when two categories has same position value
    if (category.position === nextCategory.position) {
      nextCategory.position = nextCategory.position + 1
    }

    const newCategoryPosition = nextCategory.position
    const oldCategoryPosition = category.position

    category.position = newCategoryPosition
    nextCategory.position = oldCategoryPosition

    const categoriesToUpdatePosition = [category, nextCategory]

    categoryUpdatePosition({ template: template.sk, categories: categoriesToUpdatePosition })
      .then(() => {
        onSuccessCategoryUpdatePosition(parentCategory)
      }, onErrorCategoryUpdatePosition)
      .finally(() => setIsLoadingBackdrop(false))
  }, [template.sk, categoryUpdatePosition, onSuccessCategoryUpdatePosition])

  const onSwitchVisibility = (category, isVisible, index, onErrorCb) => {
    setIsLoadingBackdrop(true)
    categoryToggle({
      deltaType: 'category',
      template: template.sk,
      category: category.sk,
      metadata: { isVisible }
    }).then(
      () => {
        const actionMsg = isVisible ? 'enabled' : 'disabled'
        BannerStatic.show({ label: `The Category was successfully ${actionMsg}.` })
        category.isVisible = isVisible
      },
      (...args) => {
        console.error('categoryRename.registerAdhocOnErrorListener', args)
        BannerStatic.show({ label: 'Error disabling/enabling category', isError: true })
        onErrorCb()
      }
    ).finally(() => setIsLoadingBackdrop(false))
  }

  const onMoveCategoryParenthood = React.useCallback((category, hierarchy) => {
    setIsLoadingBackdrop(true)
    categoryUpdateParenthood({ template: template.sk, category: category.sk, newParent: newParentCategorySk, newCategoryPosition })
      .then(() => {
        onSuccessCategoryUpdateParenthood(hierarchy)
      }, onErrorCategoryUpdateParenthood)
      .finally(() => setIsLoadingBackdrop(false))
  }, [newParentCategorySk, newCategoryPosition, template.sk, categoryUpdateParenthood, onSuccessCategoryUpdateParenthood])

  if (!template || catGetGroupCatByTemplate.isWorking || isLoading) {
    return (
      <Grid container justifyContent='center' alignItems='center' gap={1}>
        <CircularProgress size={35} />
        <Typography variant='body2' color='textSecondary'>
          Loading Template Structure...
        </Typography>
        <Box sx={{ marginY: 4 }} />
      </Grid>
    )
  }

  return (
    <>
      <Backdrop className={classes.backdrop} open={isLoadingBackdrop}>
        <CircularProgress size={60} />
      </Backdrop>
      <ManageCategoryModal
        open={openManageCategory}
        template={template}
        category={managedCategory}
        onClose={() => setOpenManageCategory(false)}
      />
      <HandleCategoryMove
        data={moveCategoryData}
        newParentCategorySk={newParentCategorySk}
        setNewParentCategorySk={setNewParentCategorySk}
        newCategoryPosition={newCategoryPosition}
        setNewCategoryPosition={setNewCategoryPosition}
        onMove={onMoveCategoryParenthood}
        open={openMoveCategory}
        onCancel={() => setOpenMoveCategory(false)}
      />
      <AddCategoryModal
        open={openAddCategory}
        data={addCategoryData}
        onClose={handleCloseCatModal}
        onCancel={handleCancelCatModal}
        isWorking={categoryAddToTemplate.isWorking}
      />
      {openRenameCategory && (
        <EditCategoryModal
          open={openRenameCategory}
          category={renameCategoryData}
          onClose={handleCloseRenameCatModal}
          onCancel={handleCancelRenameCatModal}
          isWorking={categoryRename.isWorking}
        />
      )}
      <div className={classes.root}>
        <Grid container className={classes.titleCtn}>
          <Grid item xs={10}>
            <Typography variant='subtitle1'>{template.name}</Typography>
          </Grid>
          {!viewOnly && onChangeTemplate && (
            <Grid container item xs={2} justifyContent='flex-end'>
              <ActionButton variant='secundary' dataTest='change-template-button' onClick={onChangeTemplate}>
                Change Template
              </ActionButton>
            </Grid>
          )}
        </Grid>
        <Grid container className={classes.openAllCtn} justifyContent='flex-end'>
          <FormControlLabel
            control={<Switch color='primary' />}
            label='Open all'
            labelPlacement='start'
            data-test='open-all-button'
            onChange={handleOpenAll}
          />
        </Grid>
        <Grid container className={classes.categoriesCtn} direction='row'>
          {hierarchy.map((category, idx) => (
            <CategoryItem
              key={category.sk}
              index={idx}
              first={idx === 0}
              last={(idx + 1) === hierarchy.length}
              category={category}
              previousCategory={idx > 0 ? hierarchy[idx - 1] : null}
              nextCategory={idx < hierarchy.length - 1 ? hierarchy[idx + 1] : null}
              type='group'
              openAll={openAll}
              viewOnly={viewOnly}
              template={template}
              onManageCategory={onManageCategory}
              onAddCategory={onOpenAddCategory}
              onRenameCategory={onOpenRenameCategory}
              onOrderUpCategory={onOrderUpCategory}
              onOrderDownCategory={onOrderDownCategory}
              onSwitchVisibility={onSwitchVisibility}
              onMoveCategory={onMoveCategory}
              hierarchy={hierarchy}
            />
          ))}
          {(!viewOnly) && (
            <Grid
              item
              container
              className={classes.addCategoryCtn}
              direction='row'
              alignItems='center'
              onClick={() => onOpenAddCategory({ startPosition: hierarchy.length })}
            >
              <AddCircleOutlineIcon color='primary' className={classes.addIcon} />
              <Typography variant='body2' color='primary'>New Group Category</Typography>
            </Grid>
          )}
        </Grid>
      </div>
    </>
  )
}

export default TemplateHierarchy
